/**
 * Custom hook for managing pagination state.
 *
 * Eliminates the repeated pattern of:
 *   const [currentPage, setCurrentPage] = useState(1);
 *   const [totalPages, setTotalPages] = useState(1);
 *   const [totalCount, setTotalCount] = useState(0);
 *   const pageSize = 12;
 *   const handlePageChange = (newPage: number) => { ... }
 *
 * Usage:
 *   const pagination = usePagination({ pageSize: 20 });
 *
 *   // After API call:
 *   pagination.setFromResponse(response.data.count);
 *
 *   // In pagination component:
 *   <Pagination
 *     currentPage={pagination.currentPage}
 *     totalPages={pagination.totalPages}
 *     onPageChange={pagination.goToPage}
 *   />
 */

import { useState, useCallback, useMemo, useRef } from 'react';

/**
 * What a page-size request remembers between fetches: the size that was asked
 * for and the size the server actually served for it. Kept per requested size
 * so changing the rows-per-page control starts a fresh measurement.
 */
export interface PageSizeMemory {
  requested: number;
  served: number;
}

/**
 * CR-7279: the page count a response actually implies.
 *
 * Every list used to divide the response `count` by the page size it ASKED
 * for. The API clamps a page (100 rows on the standard paginator, 1000 on the
 * large one), so asking for 500 gets 100 back and `ceil(count / 500)` is a
 * fifth of the real page count — the pager renders too few buttons and the
 * tail of the list is unreachable, with no error.
 *
 * The divisor is derived from what came back: when a page is shorter than the
 * size believed to be in force AND rows still exist beyond it, the server's
 * clamp is the real page size. A short page with nothing beyond it is simply
 * the last page, which is why the served size is remembered across pages via
 * `memory` — recomputing on the last page from its own length would shrink
 * the pager under the user's feet.
 *
 * @param count - total rows reported by the API
 * @param requested - the page size sent in the request
 * @param returned - rows actually in this response
 * @param page - the 1-indexed page that was fetched
 * @param memory - the previous result's memory, if the request size is unchanged
 * @returns the page count (0 for an empty set, as the lists have always shown)
 *   and the memory to keep for the next call
 */
export function pageCountFromResponse(
  count: number,
  requested: number,
  returned: number,
  page = 1,
  memory?: PageSizeMemory,
): { totalPages: number; memory: PageSizeMemory } {
  const safeRequested = Math.max(1, requested || 1);
  const believed = memory && memory.requested === safeRequested && memory.served > 0 ? memory.served : safeRequested;
  const seenSoFar = (Math.max(1, page) - 1) * believed + returned;
  const served = returned > 0 && returned < believed && seenSoFar < count ? returned : believed;
  return {
    totalPages: Math.ceil((count || 0) / served),
    memory: { requested: safeRequested, served },
  };
}

export interface PaginationOptions {
  /** Items per page (default: 20) */
  pageSize?: number;
  /** Initial page (default: 1) */
  initialPage?: number;
  /** Scroll to top on page change (default: true) */
  scrollToTop?: boolean;
  /** Callback when page changes */
  onPageChange?: (page: number) => void;
}

export interface PaginationResult {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;

  /** Go to a specific page */
  goToPage: (page: number) => void;
  /** Go to next page */
  nextPage: () => void;
  /** Go to previous page */
  prevPage: () => void;
  /** Reset to first page */
  reset: () => void;

  /**
   * Update pagination from an API response. Pass the number of rows the page
   * actually held so the page count follows the server's clamp (CR-7279).
   */
  setFromResponse: (count: number, returned?: number) => void;
  /** Rows per page the server actually serves for the current request size */
  servedPageSize: number;
  /** Manual setters */
  setTotalCount: React.Dispatch<React.SetStateAction<number>>;
  setTotalPages: React.Dispatch<React.SetStateAction<number>>;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;

  /** Helper: is on first page */
  isFirstPage: boolean;
  /** Helper: is on last page */
  isLastPage: boolean;
  /** Helper: has multiple pages */
  hasMultiplePages: boolean;

  /** Calculate start/end indices for display */
  startIndex: number;
  endIndex: number;

  /** Update page size dynamically */
  setPageSize: (size: number) => void;
}

/**
 * Manages pagination state (current page, total pages, navigation) for list views.
 * Call `setFromResponse(count)` after an API call to synchronize total count.
 *
 * @param options - Page size, initial page, scroll behavior, and change callback
 */
export function usePagination(options: PaginationOptions = {}): PaginationResult {
  const {
    pageSize = 20,
    initialPage = 1,
    scrollToTop = true,
    onPageChange,
  } = options;

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [dynamicPageSize, setDynamicPageSize] = useState(pageSize);
  const [servedPageSize, setServedPageSize] = useState(pageSize);
  const memoryRef = useRef<PageSizeMemory | undefined>(undefined);

  const goToPage = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      if (scrollToTop) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      onPageChange?.(page);
    }
  }, [totalPages, scrollToTop, onPageChange]);

  const nextPage = useCallback(() => {
    goToPage(currentPage + 1);
  }, [currentPage, goToPage]);

  const prevPage = useCallback(() => {
    goToPage(currentPage - 1);
  }, [currentPage, goToPage]);

  const reset = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const setFromResponse = useCallback((count: number, returned?: number) => {
    setTotalCount(count);
    if (returned === undefined) {
      setTotalPages(Math.ceil(count / dynamicPageSize) || 1);
      return;
    }
    const paged = pageCountFromResponse(count, dynamicPageSize, returned, currentPage, memoryRef.current);
    memoryRef.current = paged.memory;
    setServedPageSize(paged.memory.served);
    setTotalPages(paged.totalPages || 1);
  }, [dynamicPageSize, currentPage]);

  const handleSetPageSize = useCallback((size: number) => {
    setDynamicPageSize(size);
    setServedPageSize(size);
    memoryRef.current = undefined;
    setCurrentPage(1);
    // Recalculate total pages with new size
    if (totalCount > 0) {
      setTotalPages(Math.ceil(totalCount / size) || 1);
    }
  }, [totalCount]);

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;
  const hasMultiplePages = totalPages > 1;

  const startIndex = useMemo(() => (currentPage - 1) * servedPageSize + 1, [currentPage, servedPageSize]);
  const endIndex = useMemo(() => Math.min(currentPage * servedPageSize, totalCount), [currentPage, servedPageSize, totalCount]);

  return {
    currentPage,
    totalPages,
    totalCount,
    pageSize: dynamicPageSize,
    servedPageSize,
    goToPage,
    nextPage,
    prevPage,
    reset,
    setFromResponse,
    setTotalCount,
    setTotalPages,
    setCurrentPage,
    isFirstPage,
    isLastPage,
    hasMultiplePages,
    startIndex,
    endIndex,
    setPageSize: handleSetPageSize,
  };
}

/**
 * Generates a windowed array of page numbers for pagination UI display.
 *
 * @param currentPage - The currently active page (1-indexed)
 * @param totalPages - Total number of pages
 * @param maxVisible - Maximum number of page buttons to show (default: 5)
 * @returns Array of page numbers centered around the current page
 */
export function getPageNumbers(currentPage: number, totalPages: number, maxVisible = 5): number[] {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const half = Math.floor(maxVisible / 2);
  let start = currentPage - half;
  let end = currentPage + half;

  if (start < 1) {
    start = 1;
    end = maxVisible;
  } else if (end > totalPages) {
    end = totalPages;
    start = totalPages - maxVisible + 1;
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export default usePagination;
